import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CurrencyService } from '@core/services/currency.service';
import { SpecialHandlingService } from '@core/services/special-handling.service';
import { SpinnerService } from '@core/services/spinner.service';
import { TranslationService } from '@core/services/translation.service';
import { CyclesAPI } from '@core/typings/api/cycles.typing';
import { ApplicationByFormFromApi, ApplicationCycle, ApplyPageApplication, EligibilityResponse, MyApplicationForUi, MyApplicationFromApi, RoutingOrEligibilityChangesToSave, SaveApplication, SaveApplicationResponse, SubmitEligibility } from '@core/typings/application.typing';
import { ClientBrandingFromApi, ColorPaletteType } from '@core/typings/branding.typing';
import { ApplicantOrganizationForUi } from '@core/typings/organization.typing';
import { ProcessingTypes } from '@core/typings/payment.typing';
import { CanOrgSubmitMultipleAppsOptions, ProgramApplicantType, ProgramDetail, ProgramDetailApi } from '@core/typings/program.typing';
import { ApplicationStatuses } from '@core/typings/status.typing';
import { FormTranslations } from '@core/typings/translation.typing';
import { CancelApplicationModalResponse, CancelApplicationPayload } from '@core/typings/ui/cancel-application.typing';
import { ReferenceFieldsUI } from '@core/typings/ui/reference-fields.typing';
import { ApplicantCommunicationService } from '@features/applicant-communications/services/applicant-communications.service';
import { CommunicationForApplicant } from '@features/applicant-communications/typings/applicant-communications.typing';
import { ApplicationFormService } from '@features/application-forms/services/application-forms.service';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { FormForApplicantForUI, FormStatuses, FormTypes, MyApplicationFormUI, ProgramFormForUi } from '@features/configure-forms/form.typing';
import { CyclesService } from '@features/cycles/cycles.service';
import { EmployeeSSOFieldsService } from '@features/employee-sso-fields/employee-sso-fields.service';
import { EmployeeSSOFieldsData } from '@features/employee-sso-fields/employee-sso-fields.typing';
import { FormFieldHelperService } from '@features/form-fields/services/form-field-helper.service';
import { FormFieldService } from '@features/form-fields/services/form-field.service';
import { FormHelperService } from '@features/forms/services/form-helper/form-helper.service';
import { FormLogicService } from '@features/forms/services/form-logic/form-logic.service';
import { InKindService } from '@features/in-kind/in-kind.service';
import { NonprofitService } from '@features/nonprofit/nonprofit.service';
import { ProgramAutomationForApplicantUi, RoutingAppForSave } from '@features/programs/program-automation/program-automation.typing';
import { ProgramService } from '@features/programs/services/program.service';
import { Base64 } from '@yourcause/common';
import { DateService } from '@yourcause/common/date';
import { I18nService } from '@yourcause/common/i18n';
import { LogService } from '@yourcause/common/logging';
import { ConfirmAndTakeActionService } from '@yourcause/common/modals';
import { NotifierService } from '@yourcause/common/notifier';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { ArrayHelpersService } from '@yourcause/common/utils';
import { isAfter, isSameSecond } from 'date-fns';
import { ApplicationApplicantResources } from './application-applicant.resources';
import { ApplicationApplicantState } from './application-applicant.state';

@AttachYCState(ApplicationApplicantState)
@Injectable({ providedIn: 'root' })
export class ApplicationApplicantService extends BaseYCService<ApplicationApplicantState> {

   constructor (
    private logger: LogService,
    private programService: ProgramService,
    private clientSettingsService: ClientSettingsService,
    private spinnerService: SpinnerService,
    private router: Router,
    private formFieldService: FormFieldService,
    private translationService: TranslationService,
    private cyclesService: CyclesService,
    private applicationApplicantResources: ApplicationApplicantResources,
    private i18n: I18nService,
    private arrayHelper: ArrayHelpersService,
    private notifier: NotifierService,
    private formHelperService: FormHelperService,
    private nonprofitService: NonprofitService,
    private applicationFormService: ApplicationFormService,
    private formLogicService: FormLogicService,
    private employeeSSOFieldsService: EmployeeSSOFieldsService,
    private specialHandlingService: SpecialHandlingService,
    private confirmAndTakeActionService: ConfirmAndTakeActionService,
    private currencyService: CurrencyService,
    private formFieldHelperService: FormFieldHelperService,
    private inKindService: InKindService,
    private dateService: DateService,
    private applicantCommunicationService: ApplicantCommunicationService
  ) {
    super();

    this.i18n.language$.subscribe(() => {
      if (!!this.myApplications) {
        // if the langauge changes, we need to re-translate the applicant and nomination description text
        this.adaptAndSetMyApplications(this.myApplications);
      }
    });
  }

  get myApplications () {
    return this.get('myApplications');
  }

  get revisionAlerts () {
    return this.get('revisionAlerts');
  }

  get formsPastDue () {
    return this.get('formsPastDue');
  }

  get hasNominations () {
    return this.get('hasNominations');
  }

  get currentApp () {
    return this.get('currentApp');
  }

  get currentProgram () {
    return this.get('currentProgram');
  }

  get eligibilityResponseToSave (): RoutingOrEligibilityChangesToSave {
    const b64 = sessionStorage.getItem('eligibilityResponseToSave');

    if (b64) {
      return JSON.parse(Base64.decode(b64));
    }

    return null;
  }

  /**
   * Sets the Eligibility Response to Save
   *
   * @param app: Application to set
   */
  setEligibilityResponseToSave (app: RoutingOrEligibilityChangesToSave) {
    const b64 = Base64.encode(JSON.stringify(app));

    sessionStorage.setItem('eligibilityResponseToSave', b64);
  }

  /**
   * Clears the Eligibility Response to Save
   */
  clearEligibilityResponseToSave () {
    sessionStorage.removeItem('eligibilityResponseToSave');
  }

  /**
   * Sets My Applications
   *
   * @param myApplications: My Applications to set
   */
  setMyApplications (myApplications: MyApplicationForUi[]) {
    this.set('myApplications', myApplications);
  }

  /**
   * Sets Revision Alerts
   *
   * @param revisionAlerts: Revision Alerts to set
   */
  setRevisionAlerts (revisionAlerts: MyApplicationFormUI[]) {
    this.set('revisionAlerts', revisionAlerts);
  }

  /**
   * Gets the latest vetting info for the application / org
   *
   * @param appId: Application ID
   * @param formId: Form ID
   * @returns the latest vetting request status of the application
   */
  async getLatestVettingStatusForApplication (appId: number, formId: number) {
    const application = await this.getApplicationByForm(
      appId,
      formId
    );

    return application.latestVettingRequestStatusForOrg;
  }

  async getApplicationStatus (
    appId: number,
    formId: number
  ) {
    const app = await this.applicationApplicantResources.getApplicationByForm(
      appId,
      formId
    );

    return app.applicationStatusId;
  }

  /**
   * Returns the application with form info
   *
   * @param appId: Application ID
   * @param formId: Form ID
   * @returns the application with form info
   */
  async getApplicationByForm (
    appId: number,
    formId: number
  ) {
    try {
      const application = await this.applicationApplicantResources.getApplicationByForm(
        appId,
        formId
      );

      return application;
    } catch (e) {
      this.logger.error(e);
      this.notifier.error(
        this.i18n.translate(
          'common:textErrorLoadingApplication',
          {},
          'There was an error loading the application'
        )
      );

      return null;
    }
  }

  async preparePortalApplication (
    appId: number,
    formId: number,
    programId: string
  ) {
    try {
      let application: ApplicationByFormFromApi;
      if (!!appId) {
        application = await this.applicationApplicantResources.getApplicationByForm(
          appId,
          formId
        );
      }
      const progId = (programId || application?.grantProgramId);
      const [
        program
      ] = await Promise.all([
        this.programService.getAndAdaptProgramForApplicant(progId),
        this.getMyApplications()
      ]);
      this.set('currentApp', application);
      this.set('currentProgram', program);
    } catch (e) {
      this.onApplicationLoadingError(e as HttpErrorResponse);
    }
  }

  /**
   * Returns the application for the apply page
   *
   * @param appId: Application ID
   * @param formId: Form ID
   * @param programOrRoutingId: Program Routing ID
   * @param orgId: Organization ID
   * @param isRouting: is for routing?
   * @returns the portal application for the apply page
   */
  async setPortalApplication (
    appId: number,
    formId: number,
    programOrRoutingId: string,
    orgId: number,
    isRouting: boolean,
    routingAppForSave?: RoutingAppForSave,
    copyOfId?: number
  ) {
    const application = this.currentApp;
    let appToCopy: ApplicationByFormFromApi;
    const program: ProgramDetail = this.currentProgram;
    let routing: ProgramAutomationForApplicantUi;
    let employeeInfo = {} as EmployeeSSOFieldsData;
    let referenceFields: ReferenceFieldsUI.RefResponseMap = {};
    let clientBranding: ClientBrandingFromApi;
    let clientId: number;
    let formRevisionId: number;
    let eligibilityRevisionId: number;
    let cycle: ApplicationCycle;
    let form: ProgramFormForUi;
    let forms: FormForApplicantForUI[] = [];
    let eligibilityForm: ProgramFormForUi;
    let organization: ApplicantOrganizationForUi;
    let communications: CommunicationForApplicant[] = [];
    try {
      const isCopy = !!copyOfId;
      // Standard Program Apply - not routing
      if (!isRouting) {
        clientId = program.clientId;
        if (isCopy) {
          appToCopy = await this.applicationApplicantResources.getApplicationByForm(
            copyOfId,
            program.form.formId
          );
        }
        cycle = this.getCycleForApplyPage(
          application?.cycle,
          program.grantProgramId,
          program.cycles
        );

        formRevisionId = application?.form?.formRevisionId ||
          appToCopy?.form?.formRevisionId ||
          program.form.formRevisionId;
        eligibilityRevisionId = !application ?
          program.eligibilityForm?.formRevisionId :
          undefined;
        const hasApp = !!application;
          // When applying with invitation. The org could be tied to the invitation but app not created yet
        const noAppButHasOrg = !appId && !!orgId;
        const foundApp = this.myApplications?.find((app) => {
          return +app.applicationId === +appId;
        });
        const [
          _employeeInfo,
          _organization,
          _forms
        ] = await Promise.all([
          hasApp || isCopy ? this.employeeSSOFieldsService.getEmployeeSSOFieldsForApp(appId || copyOfId) : {} as EmployeeSSOFieldsData,
          noAppButHasOrg ? this.nonprofitService.getApplicantOrganizationFromOrgId(+orgId, program.clientId) : undefined,
          !!appId ? this.applicationFormService.getAllApplicantForms(appId, application.applicationStatusId) : [],
          this.prepareReferenceFields(
            formRevisionId,
            eligibilityRevisionId,
            routingAppForSave?.formRevisionId
          ),
          this.clientSettingsService.setAvailableApplicantCurrencies(program.grantProgramId),
          !!appId && !!foundApp && foundApp.partnerId !== 1 ?
            this.applicantCommunicationService.doSetApplicationCommunications(appId) :
            []
        ]);
        communications = this.applicantCommunicationService.applicationCommunicationsMap[appId] ?? [];
        employeeInfo = _employeeInfo;
        forms = _forms;
        if (!!_organization) {
          organization = _organization;
        } else {
          organization = application?.organization || appToCopy?.organization;
        }
        eligibilityForm = this.getEligibilityForm(program, application);
        const _form = application?.form || program?.form;
        const adaptedResponse = this.formLogicService.adaptFormDefinitionForTabs(_form.formDefinition, _form.formType);
        const foundForm = forms.find((f) =>  f.formId === _form.formId);
        let dueDate = null;
        let isOverdue = false;
        if (!!foundForm) {
          dueDate = foundForm.dueDate;
          const result = this.applicationFormService.getIsFormOverdueAndAdaptDate(dueDate);
          dueDate = result.dueDate;
          isOverdue = result.isOverdue;
        }
        form = {
          ..._form,
          dueDate,
          isOverdue,
          formDefinition: adaptedResponse.formDefinition,
          customJavascriptComps: [],
          defaultLanguageId: program?.form?.defaultLanguageId
        };
        if (hasApp) {
          referenceFields = await this.formFieldService.getReferenceFieldResponses(
            appId,
            application?.form?.applicationFormId,
            form.formDefinition,
            this.formHelperService.getTableAndSubsetIdsFromFormDefinition(
              [form.formDefinition]
            ),
            null,
            false,
            false,
            undefined,
            eligibilityForm?.formData
          );
          const translations = this.translationService.viewTranslations.FormTranslation[
            formId || program.form.formId
          ];
          this.appendFormTranslationsToApp(
            application,
            translations
          );
        }
      } else {
        // Routing Scenario
        routing = await this.getRoutingInfoForApplicant(programOrRoutingId);
        formRevisionId = routing.formRevisionId;
        await this.prepareReferenceFields(null, null, formRevisionId);
        clientId = routing.clientId;
        form = routing.form;
      }
      this.clientSettingsService.clearRestrictedRegistrationAuthorities();

      const [
        _clientBranding
      ] = await Promise.all([
        this.getClientBranding(clientId),
        this.clientSettingsService.setClientSettingsForApplicant(clientId),
        this.inKindService.getCategoriesForApplicant(clientId)
      ]);
      clientBranding = _clientBranding;

      const adaptedApp = this.adaptToPortalApplication(
        appId,
        application,
        program,
        organization,
        clientId,
        form,
        eligibilityForm,
        cycle,
        employeeInfo,
        referenceFields,
        clientBranding,
        routing,
        communications
      );

      return {
        application: adaptedApp,
        forms
      };
    } catch (e) {
      this.onApplicationLoadingError(e as HttpErrorResponse);

      return null;
    }
  }

  onApplicationLoadingError (e: HttpErrorResponse) {
    this.logger.error(e);
    this.spinnerService.stopSpinner();
    this.notifier.error(this.i18n.translate(
      'common:textErrorLoadingApplication',
      {},
      'There was an error loading the application'
    ))
    this.router.navigate([
      '/apply/applications'
    ]);
  }

  /**
   * Fetched when an applicant navigates to the routing link.
   *
   * @param landingLinkGuid: Landing Link Guid
   * @returns the program automation routing details
   */
  async getRoutingInfoForApplicant (
    landingLinkGuid: string
  ): Promise<ProgramAutomationForApplicantUi> {
    const details = await this.applicationApplicantResources.getRoutingInfoForApplicant(landingLinkGuid);
    details.formDefinition = this.formLogicService.adaptFormDefinitionForTabs(details.formDefinition, FormTypes.ROUTING).formDefinition;

    return {
      ...details,
      form: {
        applicationFormId: null,
        formId: details.formId,
        formRevisionId: details.formRevisionId,
        name: details.name,
        description: details.description,
        formData: null,
        isDraft: true,
        canEdit: true,
        revisionNotes: '',
        formType: FormTypes.ROUTING,
        applicationFormStatusId: null,
        defaultLanguageId: '',
        requireSignature: false,
        signatureDescription: '',
        formDefinition: details.formDefinition,
        customJavascriptComps: [],
        dueDate: null,
        isOverdue: false
      }
    };
  }
  
  /**
   * Returns the relevant eligibility form
   *
   * @param program: Program
   * @param application: Application
   * @returns the relevant eligibility form
   */
  getEligibilityForm (
    program: ProgramDetailApi,
    application: ApplicationByFormFromApi
  ) {
    let eligibilityForm: ProgramFormForUi = null;
    if (program?.eligibilityForm) {
      const adaptedResponse = this.formLogicService.adaptFormDefinitionForTabs(program.eligibilityForm.formDefinition, FormTypes.ELIGIBILITY);
      eligibilityForm = {
        ...program.eligibilityForm,
        formDefinition: adaptedResponse.formDefinition,
        customJavascriptComps: adaptedResponse.customJavascriptComps,
        dueDate: null,
        isOverdue: false
      };
    }

    const isExistingEligibility = application?.form?.formType === FormTypes.ELIGIBILITY;
    if (isExistingEligibility) {
      const response = this.formLogicService.adaptFormDefinitionForTabs(application.form.formDefinition, FormTypes.ELIGIBILITY);
      eligibilityForm = {
        ...application.form,
        formDefinition: response.formDefinition,
        customJavascriptComps: response.customJavascriptComps,
        dueDate: null,
        isOverdue: false
      };
    }

    return eligibilityForm;
  }

  /**
   * Adapts to the portal application for the apply page
   *
   * @param id: Application ID
   * @param application: Application
   * @param appToCopy; Application to Copy
   * @param program: Program
   * @param organization: Organization
   * @param clientId: Client ID
   * @param form: Form
   * @param eligibilityForm: Eligibility Form
   * @param cycle: Cycle
   * @param employeeInfo: Employee SSO Info
   * @param referenceFields: Reference Field answers
   * @param clientBranding: Client Branding info
   * @param routing: Routing info
   * @returns the adapted application for apply page
   */
  adaptToPortalApplication (
    id: number,
    application: ApplicationByFormFromApi,
    program: ProgramDetail,
    organization: ApplicantOrganizationForUi,
    clientId: number,
    form: ProgramFormForUi,
    eligibilityForm: ProgramFormForUi,
    cycle: ApplicationCycle,
    employeeInfo: EmployeeSSOFieldsData,
    referenceFields: ReferenceFieldsUI.RefResponseMap,
    clientBranding: ClientBrandingFromApi,
    routing: ProgramAutomationForApplicantUi,
    communications: CommunicationForApplicant[]
  ): ApplyPageApplication {
    let amountRequested = application ?
      application.amountRequested :
      null;
    let currencyRequestedAmountEquivalent = application ?
      application.currencyRequestedAmountEquivalent :
      null;
    const isClientProcessing = !!routing ? true : cycle?.isClientProcessing;
    if (!id) {
      // For new apps, we should grab the default value from the amount requested component
      // So that gets saved to the application when created
      amountRequested = this.formHelperService.findDefaultAmountRequestedValOnForm(
        form.formDefinition,
        amountRequested
      );
      currencyRequestedAmountEquivalent = this.formHelperService.findDefaultAmountRequestedValOnForm(
        form.formDefinition,
        currencyRequestedAmountEquivalent
      );
    }

    return {
      id,
      applicationId: id,
      applicationFormId: application?.form?.applicationFormId,
      cycles: program?.cycles ?? [],
      isApplicationDraft: application ? application.isApplicationDraft : true,
      organization,
      programId: program?.grantProgramId,
      fallbackProgramGuid: routing?.fallbackGrantProgramGuid,
      ruleSetFailureMessage: routing?.ruleSetFailureMessage,
      charityBucketId: program?.charityBucketId,
      charityBucketDescription: program?.charityBucketDescription,
      grantProgramName: program?.grantProgramName,
      grantProgramDescription: program?.grantProgramDescription,
      grantProgramGuidelines: program?.grantProgramGuidelines,
      programApplicantType: program?.programApplicantType ??
        routing?.programApplicantType ??
        ProgramApplicantType.INDIVIDUAL,
      clientName: program?.clientName || routing?.clientName,
      clientLogoUrl: program?.logoUrl || program?.clientLogoUrl || routing?.clientLogoUrl,
      clientId,
      form,
      formId: form.formId,
      revisionId: form.formRevisionId,
      eligibilityForm,
      programAllowCollaboration: !id ?
        null :
        application.programAllowCollaboration,
      programSendEmailsToCollaborators: !id ?
        null :
        application.programSendEmailsToCollaborators,
      hasDraftApplication: program?.hasDraftApplication ?? false,
      passMessage: program?.eligibilityPassMessage || this.programService.passMessage,
      failMessage: program?.eligibilityFailMessage || this.programService.failMessage,
      clientOrganizationsProcessingTypeId: cycle?.isClientProcessing ?
        ProcessingTypes.Client :
        ProcessingTypes.YourCause,
      formType: application ? application.form.formType : FormTypes.REQUEST,
      hideCycleDatesInApplicantPortal: program?.hideCycleDatesInApplicantPortal,
      amountRequested,
      currencyRequestedAmountEquivalent,
      amountRequestedForEdit: currencyRequestedAmountEquivalent,
      currencyRequested: application ?
        application.currencyRequested :
        null,
      allowAddOrg: !!routing ?
        false :
        typeof program?.allowAddOrg !== 'boolean' ? true : program?.allowAddOrg,
      nominee: application ?
        application.nominee :
        null,
      statusId: application ?
        application.applicationStatusId :
        ApplicationStatuses.Draft,
      nominationApplicationId: application ?
        application.nominationApplicationId :
        null,
      designation: application ?
        application.paymentDesignation :
        '',
      careOf: application ?
        application.careOf :
        '',
      specialHandling: this.specialHandlingService.getSpecialHandling(application),
      employeeInfo,
      isArchived: application ? application.isArchived : false,
      isProgramArchived: program?.isArchived,
      referenceFields,
      inKindAmountRequested: application ?
        application.inKindAmountRequested :
        0,
      inKindItems: application ?
        application.inKindItems :
        [],
      cycle,
      defaultFormId: program?.form.formId,
      latestVettingRequestStatusForOrg: application?.latestVettingRequestStatusForOrg,
      invitationOrganizationId: application?.invitationOrganizationId,
      clientBranding,
      isClientProcessing,
      multipleApplicationsLimitType: program?.multipleApplicationsLimitType || CanOrgSubmitMultipleAppsOptions.Yes,
      multipleApplicationsLimitMessage: program?.multipleApplicationsLimitMessage,
      allowedOrgLocations: program?.allowedOrgLocations || [],
      communications
    };
  }

  /**
   * Fetches necessary reference field info based on the revision id(s)
   *
   * @param formRevisionId: Form revision ID
   * @param eligibilityFormRevisionId: Eligibility form revision ID
   * @param routingFormRevisionId: Routing form revision ID
   */
  async prepareReferenceFields (
    formRevisionId: number,
    eligibilityFormRevisionId: number,
    routingFormRevisionId: number
  ) {
    const [
      formFields,
      eligibilityFields,
      routingFields
    ] = await Promise.all([
      this.formFieldService.fetchFieldsByFormRevisionId(formRevisionId),
      this.formFieldService.fetchFieldsByFormRevisionId(eligibilityFormRevisionId),
      this.formFieldService.fetchFieldsByFormRevisionId(routingFormRevisionId)
    ]);

    this.formFieldHelperService.setAllReferenceFields([
      ...formFields,
      ...eligibilityFields,
      ...routingFields
    ]);
  }

  /**
   * Finds the relevant cycle when applying for a program
   *
   * @param appCycle: Application Cycle
   * @param grantProgramId: Grant Program ID
   * @param programCycles: Program Cycles
   * @returns the relevant cycle when applying
   */
  getCycleForApplyPage (
    appCycle: ApplicationCycle,
    grantProgramId: number,
    programCycles: CyclesAPI.BaseProgramCycle[]
  ) {
    let cycle: ApplicationCycle;
    if (appCycle) {
      cycle = appCycle;
    } else {
      const {
        currentCycle
      } = this.cyclesService.getCycleDateHelpers(programCycles);
      if (currentCycle) {
        cycle = {
          id: currentCycle.id,
          startDate: currentCycle.startDate,
          endDate: currentCycle.endDate,
          grantProgramId,
          isArchived: currentCycle.isArchived,
          isClientProcessing: currentCycle.isClientProcessing,
          clientOrganizationsProcessingTypeId: currentCycle.clientOrganizationsProcessingTypeId
        };
      }
    }

    return cycle;
  }

  /**
   * Adds the form translation values to the application forms
   *
   * @param application: Application to adapt
   * @param translations: translations to append
   */
  appendFormTranslationsToApp (
    application: ApplicationByFormFromApi,
    translations: FormTranslations
  ) {
    application.form.name = translations && translations.Name ?
      translations.Name :
      application.form.name;
    application.form.description = translations && translations.Description ?
      translations.Description :
      application.form.description;
  }

  /**
   * Returns client branding info by client ID
   *
   * @param clientId: Client ID
   * @returns client branding info
   */
  async getClientBranding (clientId: number) {
    const branding = await this.applicationApplicantResources.getClientBranding(clientId);
    this.clientSettingsService.setClientBranding({
      ...branding,
      chartPrimary: branding.brandPrimary,
      chartSecondary: branding.brandSecondary,
      chartUtility: branding.brandUtility,
      colorPalette: ColorPaletteType.STANDARD
    });

    return branding;
  }

  /**
   * Clears client branding
   */
  clearClientBranding () {
    this.clientSettingsService.setClientBranding(undefined);
  }

  /**
   * Gets My Applications
   *
   * @param force: force the endpoint call?
   */
  async getMyApplications (force = false) {
    if (force || !this.myApplications) {
      const data = await this.applicationApplicantResources.getApplications(
        1,
        1000,
        true
      );
      this.adaptAndSetMyApplications(data.data);
    }
  }

  adaptAndSetMyApplications (apps: MyApplicationFromApi[]) {
    const {
      rows,
      hasNominations,
      revisionAlerts,
      formsPastDue
    } = this.adaptMyApplications(apps);

    this.set('myApplications', rows);
    this.set('revisionAlerts', revisionAlerts);
    this.set('formsPastDue', formsPastDue);
    this.set('hasNominations', hasNominations);
  }

  /**
   * Get Available Actions for App
   *
   * @param status: Application Status
   * @param hasOpenCycle: Cycle is Open
   * @param allowCollaboration: Collaboration allowed
   * @param programIsInvitationOnly: Program is Invite Only
   * @param applicantCanCopy: Grant program allows applicants to copy
   * @returns the available actions for the app
   */
  getAvailableActionsForApp (
    status: ApplicationStatuses,
    hasOpenCycle: boolean,
    allowCollaboration: boolean,
    programIsInvitationOnly: boolean,
    isProgramArchived: boolean,
    applicantCanCopy: boolean
  ) {
    const isDraft = status === ApplicationStatuses.Draft;
    const canManageApplicants = allowCollaboration &&
      status !== ApplicationStatuses.Canceled;
    const canCopy = applicantCanCopy &&
      hasOpenCycle &&
      !isDraft &&
      !programIsInvitationOnly &&
      !isProgramArchived;
    const canDelete = isDraft;
    const canCancel = ![
        ApplicationStatuses.Canceled,
        ApplicationStatuses.Declined,
        ApplicationStatuses.Approved
      ].includes(status);

    return {
      canManageApplicants,
      canDelete,
      canCancel,
      canCopy,
      canManage: canManageApplicants || canDelete || canCancel || canCopy
    };
  }

  /**
   * Adapts My Application records
   *
   * @param records: Records to adapt
   * @returns the adapted records
   */
  adaptMyApplications (
    records: MyApplicationFromApi[]
  ): {
    rows: MyApplicationForUi[];
    hasNominations: boolean;
    revisionAlerts: MyApplicationFormUI[];
    formsPastDue: MyApplicationFormUI[];
  } {
    let hasNominations = false;
    const revisionAlerts: MyApplicationFormUI[] = [];
    const formsPastDue: MyApplicationFormUI[] = [];
    const rows = records.map<MyApplicationForUi>((app) => {
      if (!!app.nominee) {
        hasNominations = true;
      }
      const adapted: MyApplicationForUi = {
        ...app,
        ...this.getAvailableActionsForApp(
          app.status,
          app.hasOpenCycle,
          app.programAllowCollaboration,
          app.isInvitationOnlyProgram,
          app.isProgramArchived,
          app.programAllowApplicantToCopy
        ),
        status: app.status,
        files: [
          ...app.files,
          ...app.mergeDocuments.map((item) => {
            return {
              fileName: item.documentTemplateName ?
                `${item.documentTemplateName}.pdf` :
                item.fileName,
              uploadedDate: item.uploadedDate,
              uploadedBy: item.uploadedBy,
              attachmentId: item.attachmentId,
              fileUploadId: item.fileUploadId,
              attachmentType: item.attachmentType
            };
          })
        ],
        applicantDescription: this.getApplicantDescription(app),
        nominationDescription: this.getNominationDescription(app)
      };
      app.applicationForms.forEach((form) => {
        const isCanceled = app.status === ApplicationStatuses.Canceled;
        if (!form.status) {
          form.status = FormStatuses.DraftSaved;
        }
        if (form.dueDate) {
          form.dueDate = this.dateService.formatDate(this.dateService.getDateAsUtc(form.dueDate));
          const showDueDate = this.applicationFormService.isDueDateRelevant(form.status, app.status);
          if (showDueDate) {
            const result = this.applicationFormService.getIsFormOverdueAndAdaptDate(form.dueDate);
            const isOverdue = result.isOverdue;
            form.dueDate = result.dueDate;
            if (isOverdue) {
              formsPastDue.push({
                ...form,
                applicationId: app.applicationId,
                grantProgramName: app.grantProgramName,
                grantProgramId: app.grantProgramId
              });
            }
          } else {
            // Due date not relavant if submitted
            form.dueDate = null;
          }
        }
        if (
          !isCanceled &&
          app.status === ApplicationStatuses.Hold &&
          (
            form.status === FormStatuses.RevisionRequested ||
            form.status === FormStatuses.DraftSaved
          )
        ) {
          revisionAlerts.push({
            ...form,
            applicationId: app.applicationId,
            grantProgramName: app.grantProgramName,
            grantProgramId: app.grantProgramId
          });
        }
      });

      app.applicationForms = this.arrayHelper.sort(app.applicationForms, 'id');

      return adapted;
    }).sort((a, b) => {
      if (a.status === ApplicationStatuses.Hold) {
        return -1;
      }

      if (b.status === ApplicationStatuses.Hold) {
        return 1;
      }

      if (isSameSecond(new Date(a.createdDate), new Date(b.createdDate))) {
        return 0;
      }

      return isAfter(new Date(a.createdDate), new Date(b.createdDate)) ? -1 : 1;
    });

    return {
      rows,
      hasNominations,
      revisionAlerts,
      formsPastDue
    };
  }

  getApplicantDescription (
    application: MyApplicationFromApi
  ) {
    const applicant = application.applicant;
    const applicantName = applicant.firstName + ' ' + applicant.lastName;
    const onBehalfOfOrg = application.organizationId;
    const isNomination = !!application.nominee;
    const nominee = isNomination ? application.nominee : null;
    const nomineeName = nominee && nominee.firstName && nominee.lastName ?
      nominee.firstName + ' ' + nominee.lastName : null;
    if (
      !!application.amountRequested ||
      !!application.inKindAmountRequested
    ) {
      const amountCash = application.amountRequested > 0 ?
        this.currencyService.formatMoney(
          application.currencyRequestedAmountEquivalent,
          application.currencyRequested
        ) :
        null;
      const amountInKind = (application.inKindItems || []).reduce((acc, item) => {
        return +acc + +item.count;
      }, 0);
      if (onBehalfOfOrg) {
        if (isNomination) {
          return this.i18n.translate(
            'APPLY:textApplicantNominatingRequesting',
            {
              applicantName,
              organizationName: application.organizationName,
              amount: amountCash
            },
            '__applicantName__ nominating __organizationName__ for __amount__'
          );
        }
        if (amountInKind && amountCash) {
          return this.i18n.translate(
            'APPLY:textApplicantOnBehalfOfOrgRequestingCashInKind',
            {
              applicantName,
              organizationName: application.organizationName,
              amount: amountCash,
              numberOfUnits: amountInKind
            },
            '__applicantName__ requesting __amount__ and __numberOfUnits__ units on behalf of __organizationName__'
          );
        } else if (amountInKind) {
          return this.i18n.translate(
            'APPLY:textApplicantOnBehalfOfOrgRequestingInKind',
            {
              applicantName,
              organizationName: application.organizationName,
              numberOfUnits: amountInKind
            },
            '__applicantName__ requesting __numberOfUnits__ units on behalf of __organizationName__'
          );
        } else {
          return this.i18n.translate(
            'APPLY:textApplicantOnBehalfOfOrgRequesting',
            {
              applicantName,
              organizationName: application.organizationName,
              amount: amountCash
            },
            '__applicantName__ requesting __amount__ on behalf of __organizationName__'
          );
        }
      } else {
        if (amountInKind && amountCash) {
          return this.i18n.translate(
            'APPLY:textApplicantRequestingCashInKind',
            {
              applicantName,
              amount: amountCash,
              numberOfUnits: amountInKind
            },
            '__applicantName__ requesting __amount__ and __numberOfUnits__ units'
          );
        } else if (amountInKind) {
          return this.i18n.translate(
            'APPLY:textApplicantRequestingInKind',
            {
              applicantName,
              numberOfUnits: amountInKind
            },
            '__applicantName__ requesting __numberOfUnits__ units'
          );
        } else {
          return this.i18n.translate(
            'APPLY:textApplicantRequesting',
            {
              applicantName,
              amount: amountCash
            },
            '__applicantName__ requesting __amount__'
          );
        }
      }
    } else if (onBehalfOfOrg) {
      return this.i18n.translate(
        isNomination ?
          'APPLY:textApplicantNominatingOrg' :
          'APPLY:textApplicantOnBehalfOfOrg',
        {
          applicantName,
          organizationName: application.organizationName
        },
        isNomination ?
          `__applicantName__ nominating __organizationName__` :
          `__applicantName__ on behalf of __organizationName__`
      );
    } else {
      if (isNomination && nomineeName) {
        return this.i18n.translate(
          'APPLY:textApplicantNominatingIndividual',
          {
            applicantName,
            nominee: nomineeName
          },
          '__applicantName__ nominating __nominee__'
        );
      }

      return applicantName;
    }
  }

  /**
   * Get the Nomination Description for My Applications Page
   *
   * @param application: Application record
   * @returns the nomination description displayed on My Applications page
   */
  getNominationDescription (
    application: MyApplicationFromApi
  ) {
    const isNomination = !!application.nominee;
    if (isNomination && application.status === ApplicationStatuses.Approved) {
      const nominee = application.nominee;
      const nomineeName = nominee.firstName + ' ' + nominee.lastName;

      return this.i18n.translate(
        'APPLY:textNomineeHasBeenInvitedToApply',
        {
          name: nomineeName
        },
        '__name__ has been invited to apply'
      );
    } else if (application.nominatedBy) {
      return this.i18n.translate(
        'APPLY:textNominatedBy',
        {
          name: application.nominatedBy.firstName + ' ' +
            application.nominatedBy.lastName
        },
        'Nominated by __name__'
      );
    }

    return '';
  }

  /**
   * Handles confirm modal and delete of the application
   *
   * @param applicationId: Application ID
   * @param isNomination: is nomination?
   * @returns if the delete application passed
   */
  async handleDeleteApplication (
    applicationId: number,
    isNomination = false
  ) {
    const response = await this.confirmAndTakeActionService.genericConfirmAndTakeAction(
      () => this.applicationApplicantResources.deleteApplication(applicationId),
      this.i18n.translate(
        isNomination ?
          'APPLY:hdrDeleteNomination' :
          'APPLY:hdrDeleteApplication',
        {},
        isNomination ?
          'Delete Nomination' :
          'Delete Application'
      ),
      '',
      this.i18n.translate(
        isNomination ?
          'APPLY:textAreYouSureDeleteNomination' :
          'APPLY:textAreYouSureDeleteApplication',
        {},
        isNomination ?
          'Are you sure you want to delete this nomination? This action cannot be undone.' :
          'Are you sure you want to delete this application? This action cannot be undone.'
      ),
      this.i18n.translate(
        'common:btnDelete',
        {},
        'Delete'
      ),
      this.i18n.translate(
        isNomination ?
          'APPLY:textSuccessfullyDeletedNomination' :
          'APPLY:textSuccessfullyDeletedApplication',
        {},
        isNomination ?
          'Successfully deleted the nomination' :
          'Successfully deleted the application'
      ),
      this.i18n.translate(
        isNomination ?
          'APPLY:textErrorDeletingNomination' :
          'APPLY:textErrorDeletingApplication',
        {},
        isNomination ?
          'There was an error deleting the nomination' :
          'There was an error deleting the application'
      )
    );

    if (response?.passed) {
      this.spinnerService.startSpinner();
      await this.getMyApplications(true);
      this.spinnerService.stopSpinner();
    }
  }

  /**
   * Remove the given application from My Applications
   *
   * @param applicationId: Application ID
   */
  removeFromMyApplications (applicationId: number) {
    const myApps = this.myApplications ?? [];
    const myRevisions = this.revisionAlerts ?? [];
    const index = myApps.findIndex((app) => {
      return +app.applicationId === +applicationId;
    });
    if (index > -1) {
      this.set('myApplications', [
        ...myApps.slice(0, index),
        ...myApps.slice(index + 1)
      ]);
    }

    const revisionIndex = myRevisions.findIndex((app) => {
      return +app.applicationId === +applicationId;
    });
    if (revisionIndex > -1) {
      this.set('revisionAlerts', [
        ...myRevisions.slice(0, revisionIndex),
        ...myRevisions.slice(revisionIndex + 1)
      ]);
    }
  }

  /**
   * Gets the draft applications for a program
   *
   * @param grantProgramGuid: Grant program guid
   * @param programCycles: Grant program cycles
   * @returns the draft applications
   */
  getDraftAppsForProgram (
    grantProgramGuid: string,
    programCycles: CyclesAPI.BaseProgramCycle[]
  ): MyApplicationForUi[] {
    const cycleMap: Record<number, CyclesAPI.BaseProgramCycle> = {};
    programCycles.forEach((cycle) => {
      cycleMap[cycle.id] = cycle;
    });

    return this.myApplications.filter((app) => {
      const cycle = cycleMap[app.cycleId];

      return app.status === ApplicationStatuses.Draft &&
        !app.isArchived &&
        app.grantProgramGuid === grantProgramGuid &&
        !cycle?.isArchived &&
        isAfter(new Date(cycle?.endDate), new Date());
    });
  }

  /**
   * Handles saving the application changes
   *
   * @param data: Payload for save
   * @returns Endpoint response - SaveApplicationResponse model
   */
  saveApplication (data: SaveApplication): Promise<SaveApplicationResponse> {
    return this.applicationApplicantResources.saveApplication(data);
  }

  /**
   * Handles submitting eligibility answers
   *
   * @param data: Payload for submitting eligibility
   * @returns the eligibility response
   */
  submitEligibility (data: SubmitEligibility): Promise<EligibilityResponse> {
    return this.applicationApplicantResources.submitEligibility(data);
  }

  /**
   * Returns an existing submitted apps for a program
   *
   * @param programId: Program ID
   * @returns the existing submitted applications for the program
   */
  checkForExistingSubmittedApps (programId: number) {
    return this.myApplications.some((app) => {
      return +app.grantProgramId === +programId &&
        app.status !== ApplicationStatuses.Draft;
    });
  }

  /**
   * Handles canceling an application
   *
   * @param modalResponse: Cancel Application Modal Response
   */
  async cancelApplication (modalResponse: CancelApplicationModalResponse) {
    const payload: CancelApplicationPayload = {
      ...modalResponse,
      customMessage: '',
      emailOptions: {
        attachments: [],
        ccEmails: null,
        bccEmails: null,
        customMessage: ''
      }
    };
    const response = await this.confirmAndTakeActionService.genericTakeAction(
      () => this.applicationApplicantResources.cancelApplication(payload),
      this.i18n.translate(
        'common:textSuccessfullyCancelledApplication',
        {},
        'Successfully cancelled application'
      ),
      this.i18n.translate(
        'common:textProblemCancelingApplication',
        {},
        'There was a problem canceling this application'
      )
    );
    if (response.passed) {
      this.spinnerService.startSpinner();
      await this.getMyApplications(true);
      this.spinnerService.stopSpinner();
    }
  }

  /**
   * This ties the routing response to the application when it is created
   *
   * @param applicationId: Application ID
   * @param appSavePayload: The payload used to save the Application
   */
  async saveApplicantRoutingOrEligibilityResponsesToApp (
    applicationId: number,
    appSavePayload: SaveApplication,
    responseToSave: RoutingOrEligibilityChangesToSave
  ) {
    const formId = responseToSave.formId;
    const formRevisionId = responseToSave.formRevisionId;
    const data: SaveApplication = {
      ...appSavePayload,
      applicationId,
      amountRequested: responseToSave.amountRequested || 0,
      currencyRequested: responseToSave.currencyRequested,
      requiredReferenceFieldKeys: [],
      amountRequestedRequired: false,
      careOfRequired: false,
      paymentDesignationRequired: false,
      form: {
        ...appSavePayload.form,
        formId,
        formRevisionId,
        isDraft: false
      }
    };
    const res = await this.saveApplication(data);
    const response = this.formFieldService.adaptFormChangesForSave(
      responseToSave.referenceFields,
      null,
      null,
      null,
      false,
      false
    );
    await this.applicationFormService.handleSaveOfChangedFields(
      response,
      applicationId,
      formId,
      formRevisionId,
      res.applicationFormId,
      false,
      false
    );
  }

  /**
   * Get a list of applicants who have applied to a given org
   *
   * @param orgId: Organization ID
   * @returns the applicants who have applied to the org
   */
  async getApplicantsForOrg (orgId: number) {
    try {
      const applicants = await this.applicationApplicantResources.getApplicantsForOrg(orgId);

      return this.arrayHelper.sort(applicants, 'firstName');
    } catch (e) {
      this.logger.error(e);

      return [];
    }
  }
}
